import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  InputWrapper,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  List,
  Center,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import {
  IconAlertCircle,
  IconPlugConnected,
  IconPlugConnectedX,
  IconTrash,
  IconZoom,
} from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table"
import { useMemo, useState, useEffect } from "react"

import { DatadogInstallation } from "@costory/types/endpoints/integration"
import {
  datadogSchema,
  DatadogType,
  MetricsDatasourceResponses,
} from "@costory/types/endpoints/metricsDatasources"

import { DatadogSvgComponent } from "@costory/front/components/DatasourceImages"
import { useBillingDatasourcesListQuery } from "@costory/front/queries/billingDatasources"
import {
  useDeleteDatadogConfiguraionMutation,
  useSaveDatadogConfigMutation,
  useValidateDatadogMutation,
} from "@costory/front/queries/integrations"
import {
  useDatadogInstallationsQuery,
  useMetricsDatasourcesListQuery,
} from "@costory/front/queries/metricsDatasources"

const QUERY_STRING =
  "avg:system.cpu.user{*} by {host}.rollup(avg, daily, 'UTC')"

enum Status {
  Draft = "DRAFT",
  Connected = "CONNECTED",
  Failed = "FAILED",
}

const Regions = [
  { value: "datadoghq.com", label: "datadoghq.com" },
  { value: "us3.datadoghq.com", label: "us3.datadoghq.com" },
  { value: "us5.datadoghq.com", label: "us5.datadoghq.com" },
  { value: "datadoghq.eu", label: "datadoghq.eu" },
  { value: "ddog-gov.com", label: "ddog-gov.com" },
] as const

interface DatadogModalProps {
  opened: boolean
  setOpened: (open: boolean) => void
  refetch: () => void
  onStartVerification: () => void
  setVerifying: (v: boolean) => void
}

const getStatusBadgeColor = (status: Status) => {
  switch (status) {
    case Status.Draft:
      return "gray"
    case Status.Connected:
      return "green"
    case Status.Failed:
      return "red"
    default:
      return "gray"
  }
}

const DatadogModal = ({
  opened,
  setOpened,
  refetch,
  onStartVerification,
  setVerifying,
}: DatadogModalProps) => {
  const [status, setStatus] = useState<Status>(Status.Draft)
  const [saveLoading, setSaveLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const form = useForm<DatadogType>({
    initialValues: {
      name: "",
      apiKey: "",
      applicationKey: "",
      region: Regions[0].value,
    },
    validate: zodResolver(datadogSchema),
  })
  const validateDatadog = useValidateDatadogMutation().mutateAsync
  const saveDatadogConfig = useSaveDatadogConfigMutation().mutateAsync

  useEffect(() => {
    if (opened && status !== Status.Failed) {
      setStatus(Status.Draft)
      setErrorMessage("")
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const resetStatusOnChange = () => {
    if (status === Status.Connected || status === Status.Failed) {
      setStatus(Status.Draft)
      setErrorMessage("")
    }
  }
  form.watch("apiKey", resetStatusOnChange)
  form.watch("applicationKey", resetStatusOnChange)
  form.watch("region", resetStatusOnChange)

  const handleSubmit = async (values: DatadogType) => {
    onStartVerification()
    setOpened(false)
    showNotification({
      title: "Validating Datadog configuration...",
      message: "This may take a few minutes. You can continue using the app.",
      color: "blue",
      autoClose: 5000,
    })
    validateDatadog({
      apiKey: values.apiKey,
      applicationKey: values.applicationKey,
      region: values.region,
      name: values.name,
      query: QUERY_STRING,
    })
      .then(async (res: MetricsDatasourceResponses.ValidateDatadog) => {
        if (res.isSuccess) {
          setStatus(Status.Connected)
          setSaveLoading(true)
          await saveDatadogConfig(
            {
              apiKey: values.apiKey,
              applicationKey: values.applicationKey,
              region: values.region,
              name: values.name,
              query: QUERY_STRING,
              status: Status.Connected,
              scope: res.scope,
            },
            {
              onSuccess: () => {
                refetch()
                form.reset()
                setSaveLoading(false)
                setVerifying(false)
                showNotification({
                  title: "Datadog configuration saved!",
                  message:
                    "Your Datadog configuration was validated and saved successfully.",
                  color: "green",
                })
              },
              onError: () => {
                setSaveLoading(false)
                setVerifying(false)
                showNotification({
                  title: "Error saving Datadog configuration",
                  message: "Validation succeeded but saving failed.",
                  color: "red",
                })
              },
            },
          )
        } else {
          setStatus(Status.Failed)
          setOpened(true)
          form.setValues(values)
          setVerifying(false)
          let errorMsg = "Please check your credentials and try again."
          if (res.error) errorMsg = res.error
          setErrorMessage(errorMsg)

          showNotification({
            title: "Datadog validation failed",
            message: errorMsg,
            color: "red",
          })
        }
      })
      .catch(() => {
        setVerifying(false)
      })
  }

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      title="Add Datadog Configuration"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          {errorMessage && (
            <Alert
              icon={<IconAlertCircle size="1rem" />}
              title="Validation Failed"
              color="red"
              variant="light"
              radius="md"
            >
              {errorMessage}
            </Alert>
          )}
          <InputWrapper label="Name">
            <TextInput
              placeholder="Name"
              {...form.getInputProps("name")}
              required
            />
          </InputWrapper>
          <InputWrapper label="Region">
            <Select
              data={Regions}
              disabled={saveLoading}
              {...form.getInputProps("region")}
            />
          </InputWrapper>
          <InputWrapper label="API Key">
            <TextInput
              type="password"
              placeholder="API Key"
              autoComplete="on"
              disabled={saveLoading}
              {...form.getInputProps("apiKey")}
            />
          </InputWrapper>
          <InputWrapper label="Application Key">
            <TextInput
              type="password"
              placeholder="Application Key"
              autoComplete="on"
              disabled={saveLoading}
              {...form.getInputProps("applicationKey")}
            />
          </InputWrapper>
          <Group justify="center">
            <Button type="submit" loading={saveLoading}>
              Save Config
            </Button>
          </Group>
          <Group justify="center" gap={4}>
            <Badge color={getStatusBadgeColor(status)}>{status}</Badge>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

type ConnectionData = {
  name: string
  region: string
  status: Status
  id: string
  scope?: string
}

export const DatadogIntegration = () => {
  const [opened, { open, close }] = useDisclosure(false)
  const [deletingId, setdeletingId] = useState<string | undefined>(undefined)
  const [verifying, setVerifying] = useState(false)
  const {
    mutateAsync: deleteDatadogConfiguraion,
    isPending: isDeletingDatadogConfiguraion,
  } = useDeleteDatadogConfiguraionMutation()
  const { data: datadogInstallations, refetch } = useDatadogInstallationsQuery()

  const data: ConnectionData[] = useMemo(
    () =>
      datadogInstallations?.map((_: DatadogInstallation[0]) => ({
        name: _.name,
        region: _.region,
        status: _.status as Status,
        scope: _.scope,
        id: _.id,
      })) ?? [],
    [datadogInstallations],
  )
  const metricsDatasourceQuery = useMetricsDatasourcesListQuery()
  const parsedMetricsDatasources = metricsDatasourceQuery.data
    ?.filter(({ type }) => type === "Datadog" || type === "DatadogCIVisibility")
    .map(({ metadata, name }) => ({
      name,
      datadogConfig:
        "datadogConfig" in metadata ? metadata.datadogConfig : undefined,
    }))

  const billingDatasourceQuery = useBillingDatasourcesListQuery()
  const parsedBillingDatasources = billingDatasourceQuery.data
    ?.filter(({ type }) => type === "Datadog")
    .map(({ metadata, name }) => ({
      name,
      datadogConfig:
        "datadogConfig" in metadata ? metadata.datadogConfig : undefined,
    }))

  const columns: MRT_ColumnDef<ConnectionData>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "region",
        header: "Region",
      },
      {
        accessorKey: "scope",
        header: "Scope",
        Cell: ({ cell }) => {
          const raw = cell.getValue()
          const value = typeof raw === "string" ? raw : ""
          const scopes = value.split(",").map((s) => s.trim())

          return (
            <Group gap="xs">
              {scopes.map((s, idx) => (
                <Badge
                  key={idx}
                  color={s.toLowerCase() === "billing" ? "green" : "blue"}
                  variant="light"
                  size="sm"
                >
                  {s}
                </Badge>
              ))}
            </Group>
          )
        },
      },
      {
        id: "id",
        header: "Actions",
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const id = cell.row.original.id
          const disabled = [
            ...(parsedMetricsDatasources?.filter(
              ({ datadogConfig }) => datadogConfig === id,
            ) || []),
            ...(parsedBillingDatasources?.filter(
              ({ datadogConfig }) => datadogConfig === id,
            ) || []),
          ]

          return (
            <Tooltip
              label={
                disabled?.length ? (
                  <Box>
                    <Text fw={500}>
                      This configuration is in use by the following metric or
                      billing datasources:
                    </Text>
                    <List spacing="xs" size="sm" center>
                      {disabled.map((ds) => (
                        <List.Item key={ds.name} pl="sm">
                          {ds.name}
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                ) : (
                  "Delete this configuration"
                )
              }
            >
              <ActionIcon
                color="red"
                variant="subtle"
                loading={isDeletingDatadogConfiguraion && deletingId == id}
                disabled={!!disabled?.length}
              >
                <IconTrash
                  onClick={() => {
                    modals.openConfirmModal({
                      title: "Confirm Deletion",
                      children:
                        "Are you sure you want to delete this configuration?",
                      labels: { confirm: "Delete", cancel: "Cancel" },
                      confirmProps: { color: "red" },
                      onConfirm: () => {
                        setdeletingId(id)
                        deleteDatadogConfiguraion(id, {
                          onSuccess: () => {
                            refetch()
                            setdeletingId(undefined)
                          },
                        })
                      },
                    })
                  }}
                />
              </ActionIcon>
            </Tooltip>
          )
        },
        enableSorting: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isDeletingDatadogConfiguraion,
      parsedMetricsDatasources,
      parsedBillingDatasources,
      deletingId,
    ],
  )

  const table = useMantineReactTable<ConnectionData>({
    columns,
    data,
    state: { showProgressBars: verifying },
    renderToolbarInternalActions: ({ table }) => (
      <TextInput
        placeholder="Search by name"
        value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
        onChange={(e) =>
          table.getColumn("name")?.setFilterValue(e.currentTarget.value)
        }
        leftSection={<IconZoom size={16} />}
        style={{ width: "200px" }}
        mb={8}
      />
    ),
    renderEmptyRowsFallback: () => (
      <Box
        style={{
          textAlign: "center",
        }}
        p="md"
      >
        No Datadog configurations found.
      </Box>
    ),
  })
  const isConnected = !!datadogInstallations?.filter(
    (_: DatadogInstallation[0]) => _.status == "CONNECTED",
  ).length
  return (
    <Accordion.Item value="Datadog">
      <DatadogModal
        opened={opened}
        setOpened={(v) => (v ? open() : close())}
        refetch={refetch}
        onStartVerification={() => setVerifying(true)}
        setVerifying={setVerifying}
      />
      <Accordion.Control
        icon={
          <Tooltip label={isConnected ? "Connected" : "Not Connected"}>
            <Center>
              {isConnected ? <IconPlugConnected /> : <IconPlugConnectedX />}
            </Center>
          </Tooltip>
        }
      >
        <Group>
          <DatadogSvgComponent style={{ height: 25, width: 25 }} />
          <Text size="md">Datadog</Text>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack w="100%">
          <Group>
            <Text>Configure Datadog</Text>
            <Button
              color="textMuted"
              leftSection={
                <DatadogSvgComponent
                  style={{ height: 20, width: 20 }}
                  color="textOnError"
                />
              }
              onClick={open}
            >
              Add Datadog configuration
            </Button>
          </Group>
          <Box w="100%">
            <MantineReactTable table={table} />
          </Box>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  )
}
